import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useMasterData } from "../master-data/api";
import { toUserMap, useUsers, userName } from "../users/api";
import { useOperationsLog, type OperationsLogFilters, type OperationsLogType } from "./api";
import { OPERATIONS_LOG_TYPE_LABEL_KEY, OPERATIONS_LOG_TYPES } from "./entry";
import { OperationsLogPeriod, OperationsLogTypeBadge } from "./entry-parts";
import { canWriteOperationsLog } from "./permissions";
import { CreateEntryDialog } from "./create-entry-dialog";

/** Radix Select forbids an empty item value; "every type / area" carries this token. */
const ALL = "__all__";

/**
 * The Betriebslog view (Hauptansicht 5 in SRS 4.1, [F-015]): a chronological
 * list, newest start first, with the single entry one click away. Every role
 * reads it (PO-050 option 1); recording is offered to whoever holds
 * `oplog.write`.
 */
export function OperationsLogPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const master = useMasterData();
  const usersQuery = useUsers();
  const userById = useMemo(() => toUserMap(usersQuery.data), [usersQuery.data]);

  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [type, setType] = useState<OperationsLogType | undefined>(undefined);
  const [areaId, setAreaId] = useState<string | undefined>(undefined);
  const [createOpen, setCreateOpen] = useState(false);

  // The search settles before it queries, so typing does not fire a request per key.
  useEffect(() => {
    const handle = window.setTimeout(() => setQ(search.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  const filters: OperationsLogFilters = {
    ...(q ? { q } : {}),
    ...(type ? { type } : {}),
    ...(areaId ? { areaId } : {}),
  };
  const filtered = Boolean(q || type || areaId);
  const log = useOperationsLog(filters);
  const pages = log.data?.pages ?? [];
  const items = pages.flatMap((page) => page.items);
  const lastPage = pages[pages.length - 1];

  const areaNames = (ids: readonly string[]): string =>
    ids.map((id) => master.areaById.get(id)?.name ?? "").filter(Boolean).join(", ") || t("common.none");

  const resetFilters = (): void => {
    setSearch("");
    setQ("");
    setType(undefined);
    setAreaId(undefined);
  };

  const incidentsLabel = (count: number): string =>
    count === 0
      ? t("common.none")
      : count === 1
        ? t("operationsLog.incidentsOne")
        : t("operationsLog.incidentsMany", { count });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 data-testid="operations-log_title" className="text-2xl font-semibold tracking-tight">
            {t("operationsLog.title")}
          </h1>
          <p className="max-w-2xl text-sm text-muted-foreground">{t("operationsLog.subtitle")}</p>
          {lastPage ? (
            <p data-testid="operations-log_count" className="text-sm text-muted-foreground">
              {t("operationsLog.countLine", { shown: items.length, total: lastPage.total })}
            </p>
          ) : null}
        </div>
        {canWriteOperationsLog(user) ? (
          <Button data-testid="operations-log_create" onClick={() => setCreateOpen(true)}>
            <Plus />
            {t("operationsLog.create")}
          </Button>
        ) : null}
      </div>

      <div
        role="group"
        aria-label={t("operationsLog.searchLabel")}
        data-testid="operations-log_filters"
        className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-end"
      >
        <div className="col-span-2 space-y-1.5 sm:min-w-64 sm:flex-1">
          <label htmlFor="operations-log-search" className="text-sm font-medium">
            {t("operationsLog.searchLabel")}
          </label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="operations-log-search"
              data-testid="operations-log_search"
              className="pl-9"
              value={search}
              placeholder={t("operationsLog.searchPlaceholder")}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("operationsLog.filterType")}</span>
          <Select value={type ?? ALL} onValueChange={(value) => setType(value === ALL ? undefined : (value as OperationsLogType))}>
            <SelectTrigger data-testid="operations-log_filter-type" className="w-full sm:w-48" aria-label={t("operationsLog.filterType")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("operationsLog.filterAll")}</SelectItem>
              {OPERATIONS_LOG_TYPES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(OPERATIONS_LOG_TYPE_LABEL_KEY[value])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("operationsLog.filterArea")}</span>
          <Select value={areaId ?? ALL} onValueChange={(value) => setAreaId(value === ALL ? undefined : value)}>
            <SelectTrigger data-testid="operations-log_filter-area" className="w-full sm:w-48" aria-label={t("operationsLog.filterArea")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("operationsLog.filterAll")}</SelectItem>
              {(master.areas.data ?? []).map((area) => (
                <SelectItem key={area.id} value={area.id}>
                  {area.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {filtered ? (
          <Button variant="ghost" data-testid="operations-log_reset" onClick={resetFilters}>
            {t("operationsLog.reset")}
          </Button>
        ) : null}
      </div>

      {log.isLoading ? (
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      ) : log.isError ? (
        <div
          role="alert"
          data-testid="operations-log_load-error"
          className="flex items-center justify-between gap-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm"
        >
          <span>{t("errors.load")}</span>
          <Button variant="outline" size="sm" data-testid="operations-log_retry" onClick={() => void log.refetch()}>
            {t("common.retry")}
          </Button>
        </div>
      ) : items.length === 0 ? (
        <p data-testid="operations-log_empty" className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          {filtered ? t("operationsLog.emptyFiltered") : t("operationsLog.empty")}
        </p>
      ) : (
        <>
          <div className="hidden rounded-lg border md:block">
            <Table data-testid="operations-log_table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-56">{t("operationsLog.colStart")}</TableHead>
                  <TableHead>{t("operationsLog.colType")}</TableHead>
                  <TableHead>{t("operationsLog.colDescription")}</TableHead>
                  <TableHead>{t("operationsLog.colAreas")}</TableHead>
                  <TableHead>{t("operationsLog.colRecordedBy")}</TableHead>
                  <TableHead>{t("operationsLog.colIncidents")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    data-testid={`operations-log_row_${item.id}`}
                    className="cursor-pointer"
                    onClick={() => void navigate({ to: "/operations-log/$id", params: { id: item.id } })}
                  >
                    <TableCell className="whitespace-nowrap text-sm">
                      <Link
                        to="/operations-log/$id"
                        params={{ id: item.id }}
                        data-testid={`operations-log_row-open_${item.id}`}
                        className="text-primary-text underline-offset-4 hover:underline"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <OperationsLogPeriod startAt={item.startAt} endAt={item.endAt} />
                      </Link>
                    </TableCell>
                    <TableCell>
                      <OperationsLogTypeBadge type={item.type} />
                    </TableCell>
                    <TableCell className="max-w-md whitespace-pre-wrap text-sm font-medium">{item.shortDescription}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{areaNames(item.areaIds)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{userName(item.recordedById, userById)}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {incidentsLabel(item.incidentCount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <ul className="space-y-3 md:hidden">
            {items.map((item) => (
              <li key={item.id}>
                <Link
                  to="/operations-log/$id"
                  params={{ id: item.id }}
                  data-testid={`operations-log_card_${item.id}`}
                  className="block space-y-2 rounded-lg border p-3"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <OperationsLogTypeBadge type={item.type} />
                    <span className="text-xs text-muted-foreground">
                      <OperationsLogPeriod startAt={item.startAt} endAt={item.endAt} />
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap font-medium">{item.shortDescription}</p>
                  <p className="text-sm text-muted-foreground">{areaNames(item.areaIds)}</p>
                  <p className="text-sm text-muted-foreground">
                    {userName(item.recordedById, userById)} - {incidentsLabel(item.incidentCount)}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      {log.hasNextPage ? (
        <div className="flex justify-center">
          <Button
            data-testid="operations-log_load-more"
            variant="outline"
            onClick={() => void log.fetchNextPage()}
            disabled={log.isFetchingNextPage}
          >
            {log.isFetchingNextPage ? t("common.loading") : t("common.loadMore")}
          </Button>
        </div>
      ) : null}

      {canWriteOperationsLog(user) ? <CreateEntryDialog open={createOpen} onOpenChange={setCreateOpen} /> : null}
    </div>
  );
}
